/**
 * Audio renderer.
 *
 * Streams the source in a native `<audio controls>` element via a
 * range-capable URL ({@link useFileUrl}): a short-lived `?token=` URL for
 * auth-scoped workspace files, or a presigned / public URL for attachments
 * and the Library. Streaming (not a buffered blob) is deliberate — large
 * media should start playing and seek without downloading the whole file.
 */
import { Loader2, Music } from "lucide-react";

import { useFileUrl } from "../hooks/use-file-url";
import type { FileRendererProps } from "../types";

export function AudioRenderer({ source }: FileRendererProps) {
  const state = useFileUrl(source);

  if (state.status === "loading" || state.status === "idle") {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="size-8 animate-spin text-primary" />
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
        <p>Unable to play this audio file.</p>
        <p className="text-xs opacity-70">{state.error.message}</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 p-6">
      <div className="flex size-16 items-center justify-center rounded-full bg-primary/10 text-primary">
        <Music className="size-8" />
      </div>
      <div className="max-w-full truncate text-sm font-medium text-foreground">
        {source.filename}
      </div>
      <audio controls src={state.url} className="w-full max-w-xl">
        Your browser does not support the audio element.
      </audio>
    </div>
  );
}
